import React, { useState, useEffect } from 'react';
import { CalendarClock } from 'lucide-react';
import { colors } from '../../constants/theme';
import { competitionService } from '../../services/api';
import ConfrontationCard from './ConfrontationCard';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';

/** As partidas guardam o grupo cru ('grupoA'); a tabela usa o nome amigável. */
const friendlyGroupName = (raw) => (raw || '').replace('grupo', 'Grupo ');

/**
 * Histórico das partidas da fase de grupos, com uma aba por grupo.
 *
 * Mostra só o que já aconteceu — partidas finalizadas e a que estiver em
 * andamento. As que ainda não começaram ficam de fora: quem quer ver o que vem
 * pela frente tem a aba de próximas partidas.
 */
export default function GroupHistory({ category }) {
  const [groupNames, setGroupNames] = useState([]);
  const [matchesByGroup, setMatchesByGroup] = useState({});
  const [totalsByGroup, setTotalsByGroup] = useState({});
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadHistory();
  }, [category]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await competitionService.getGroups();

      const groups = response?.data?.groups?.[category] || {};
      const allMatches = response?.data?.matches?.[category] || [];

      const names = Object.keys(groups);
      const played = {};
      const totals = {};

      for (const name of names) {
        const ofGroup = allMatches.filter(
          (m) => friendlyGroupName(m.groupName) === name,
        );
        totals[name] = ofGroup.length;
        played[name] = ofGroup.filter(
          (m) => m.status === 'finished' || m.status === 'in_progress',
        );
      }

      setGroupNames(names);
      setMatchesByGroup(played);
      setTotalsByGroup(totals);
      setSelectedGroup((current) =>
        current && names.includes(current) ? current : names[0] || null,
      );
    } catch (err) {
      // 404 = ainda não há competição com grupos sorteados
      if (err.status === 404) {
        setGroupNames([]);
        setMatchesByGroup({});
      } else {
        setError('Erro ao carregar histórico: ' + (err.error?.message || err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="medium" text="Carregando histórico..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={loadHistory} />;
  }

  if (groupNames.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <CalendarClock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-700 font-medium mb-2">Grupos ainda não sorteados!</p>
        <p className="text-sm text-gray-400">
          O histórico de partidas da categoria {category} aparecerá aqui.
        </p>
      </div>
    );
  }

  const matches = matchesByGroup[selectedGroup] || [];
  const total = totalsByGroup[selectedGroup] || 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-center gap-2">
        {groupNames.map((name) => {
          const playedCount = (matchesByGroup[name] || []).length;
          return (
            <button
              key={name}
              onClick={() => setSelectedGroup(name)}
              className={`px-4 py-2 rounded-lg font-medium text-sm ${
                selectedGroup === name ? 'text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              style={{
                backgroundColor: selectedGroup === name ? colors.primary : 'white',
              }}
            >
              {name}
              <span className="ml-2 opacity-75">
                {playedCount}/{totalsByGroup[name] || 0}
              </span>
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div
          className="text-white p-4 text-center font-bold"
          style={{ backgroundColor: colors.primary }}
        >
          {(selectedGroup || '').toUpperCase()} - CATEGORIA {category.toUpperCase()}
        </div>

        <div className="p-6">
          {matches.length === 0 ? (
            <div className="text-center py-6">
              <CalendarClock className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">
                Nenhuma partida realizada neste grupo ainda.
              </p>
              {total > 0 && (
                <p className="text-sm text-gray-400 mt-1">
                  {total} partida(s) programada(s).
                </p>
              )}
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4 text-center">
                {matches.length} de {total} partidas realizadas
              </p>
              <div className="space-y-6">
                {matches.map((match, index) => (
                  <ConfrontationCard
                    key={match.id}
                    match={match}
                    title={`Partida ${index + 1}`}
                    allowDraw
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
